import React, { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import {
  FaChartLine, FaReceipt, FaPercent,
} from "react-icons/fa";
import loancal from '../Aseets/loan calculator.svg';
import emiImg from '../Aseets/emi-cal.webp';
import investImg from '../Aseets/investing-cal.jpg';
import taxImg from '../Aseets/tax-cal.jpg';
import interestImg from '../Aseets/intrest-cal.jfif';

// ---- helpers ----
const inr = (n) => (isFinite(n) ? "₹" + Math.round(n).toLocaleString("en-IN") : "₹0");

function ResultRow({ label, value, strong }) {
  return (
    <div className="calc-result-row">
      <span>{label}</span>
      <span className={strong ? "fw-bold" : ""}>{value}</span>
    </div>
  );
}

/* ============ LOAN & EMI ============ */
function EmiCalculator() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(5);
  const r = rate / 12 / 100, n = years * 12;
  const emi = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  return (
    <div>
      <p className="text-muted-2">Calculate your monthly loan instalment (EMI) for home, car or personal loans.</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Loan Amount (₹)</Form.Label><Form.Control type="number" value={amount} onChange={(e) => setAmount(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Interest Rate (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Tenure (years)</Form.Label><Form.Control type="number" value={years} onChange={(e) => setYears(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Monthly EMI" value={inr(emi)} strong />
        <ResultRow label="Total Interest" value={inr(total - amount)} />
        <ResultRow label="Total Payment" value={inr(total)} />
      </div>
    </div>
  );
}

function LoanEligibilityCalculator() {
  const [income, setIncome] = useState(60000);
  const [existing, setExisting] = useState(0);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(20);
  const capacity = Math.max(0, income * 0.5 - existing); // 50% FOIR
  const r = rate / 12 / 100, n = years * 12;
  const loan = r === 0 ? capacity * n : (capacity * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
  return (
    <div>
      <p className="text-muted-2">Estimate the maximum loan you may be eligible for, based on your monthly income (assuming 50% can go toward EMIs).</p>
      <Row className="g-3">
        <Col md={3}><Form.Label>Monthly Income (₹)</Form.Label><Form.Control type="number" value={income} onChange={(e) => setIncome(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Existing EMIs (₹)</Form.Label><Form.Control type="number" value={existing} onChange={(e) => setExisting(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Interest Rate (%)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Tenure (years)</Form.Label><Form.Control type="number" value={years} onChange={(e) => setYears(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="EMI Capacity / month" value={inr(capacity)} />
        <ResultRow label="Eligible Loan Amount" value={inr(loan)} strong />
      </div>
    </div>
  );
}

/* ============ INVESTMENT ============ */
function SipCalculator() {
  const [monthly, setMonthly] = useState(5000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const i = rate / 12 / 100, n = years * 12;
  const invested = monthly * n;
  const maturity = i === 0 ? invested : monthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
  return (
    <div>
      <p className="text-muted-2">Estimate the future value of your monthly mutual-fund SIP investments.</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Monthly Investment (₹)</Form.Label><Form.Control type="number" value={monthly} onChange={(e) => setMonthly(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Expected Return (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Time Period (years)</Form.Label><Form.Control type="number" value={years} onChange={(e) => setYears(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Invested Amount" value={inr(invested)} />
        <ResultRow label="Estimated Returns" value={inr(maturity - invested)} />
        <ResultRow label="Total Maturity Value" value={inr(maturity)} strong />
      </div>
    </div>
  );
}

function LumpsumCalculator() {
  const [amount, setAmount] = useState(100000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const maturity = amount * Math.pow(1 + rate / 100, years);
  return (
    <div>
      <p className="text-muted-2">See how a one-time (lumpsum) investment grows over time.</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Investment (₹)</Form.Label><Form.Control type="number" value={amount} onChange={(e) => setAmount(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Expected Return (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Time Period (years)</Form.Label><Form.Control type="number" value={years} onChange={(e) => setYears(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Invested Amount" value={inr(amount)} />
        <ResultRow label="Estimated Returns" value={inr(maturity - amount)} />
        <ResultRow label="Total Value" value={inr(maturity)} strong />
      </div>
    </div>
  );
}

function FdCalculator() {
  const [principal, setPrincipal] = useState(100000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(5);
  const [freq, setFreq] = useState(4);
  const maturity = principal * Math.pow(1 + rate / 100 / freq, freq * years);
  return (
    <div>
      <p className="text-muted-2">Calculate the maturity amount of a Fixed Deposit with compound interest.</p>
      <Row className="g-3">
        <Col md={3}><Form.Label>Deposit (₹)</Form.Label><Form.Control type="number" value={principal} onChange={(e) => setPrincipal(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Rate (%)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Tenure (years)</Form.Label><Form.Control type="number" value={years} onChange={(e) => setYears(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Compounding</Form.Label>
          <Form.Select value={freq} onChange={(e) => setFreq(+e.target.value)}>
            <option value={1}>Yearly</option><option value={2}>Half-Yearly</option><option value={4}>Quarterly</option><option value={12}>Monthly</option>
          </Form.Select>
        </Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Invested Amount" value={inr(principal)} />
        <ResultRow label="Interest Earned" value={inr(maturity - principal)} />
        <ResultRow label="Maturity Amount" value={inr(maturity)} strong />
      </div>
    </div>
  );
}

function RdCalculator() {
  const [monthly, setMonthly] = useState(5000);
  const [rate, setRate] = useState(7);
  const [months, setMonths] = useState(36);
  const i = rate / 12 / 100;
  const invested = monthly * months;
  const maturity = i === 0 ? invested : monthly * ((Math.pow(1 + i, months) - 1) / i) * (1 + i);
  return (
    <div>
      <p className="text-muted-2">Estimate the maturity of a Recurring Deposit (monthly savings).</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Monthly Deposit (₹)</Form.Label><Form.Control type="number" value={monthly} onChange={(e) => setMonthly(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Rate (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Tenure (months)</Form.Label><Form.Control type="number" value={months} onChange={(e) => setMonths(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Total Deposited" value={inr(invested)} />
        <ResultRow label="Interest Earned" value={inr(maturity - invested)} />
        <ResultRow label="Maturity Amount" value={inr(maturity)} strong />
      </div>
    </div>
  );
}

/* ============ TAX ============ */
function GstCalculator() {
  const [amount, setAmount] = useState(10000);
  const [gst, setGst] = useState(18);
  const [mode, setMode] = useState("add");
  let base, tax, total;
  if (mode === "add") { base = amount; tax = (amount * gst) / 100; total = amount + tax; }
  else { base = amount / (1 + gst / 100); tax = amount - base; total = amount; }
  return (
    <div>
      <p className="text-muted-2">Add GST to an amount or remove GST from a GST-inclusive amount.</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Amount (₹)</Form.Label><Form.Control type="number" value={amount} onChange={(e) => setAmount(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>GST Rate (%)</Form.Label>
          <Form.Select value={gst} onChange={(e) => setGst(+e.target.value)}>{[3, 5, 12, 18, 28].map((g) => <option key={g} value={g}>{g}%</option>)}</Form.Select>
        </Col>
        <Col md={4}><Form.Label>Type</Form.Label>
          <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}><option value="add">Add GST</option><option value="remove">Remove GST</option></Form.Select>
        </Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Base Amount" value={inr(base)} />
        <ResultRow label={`CGST (${gst / 2}%)`} value={inr(tax / 2)} />
        <ResultRow label={`SGST (${gst / 2}%)`} value={inr(tax / 2)} />
        <ResultRow label="Total Amount" value={inr(total)} strong />
      </div>
    </div>
  );
}

function IncomeTaxCalculator() {
  const [income, setIncome] = useState(1200000);
  const [deductions, setDeductions] = useState(150000);
  const taxable = Math.max(0, income - 50000 - deductions); // standard deduction 50k
  // Old regime slabs
  let tax = 0;
  if (taxable > 1000000) tax = 112500 + (taxable - 1000000) * 0.3;
  else if (taxable > 500000) tax = 12500 + (taxable - 500000) * 0.2;
  else if (taxable > 250000) tax = (taxable - 250000) * 0.05;
  if (taxable <= 500000) tax = 0; // rebate u/s 87A
  const cess = tax * 0.04;
  return (
    <div>
      <p className="text-muted-2">Estimate your income tax (old regime, with standard deduction & 87A rebate). For guidance only.</p>
      <Row className="g-3">
        <Col md={6}><Form.Label>Annual Income (₹)</Form.Label><Form.Control type="number" value={income} onChange={(e) => setIncome(+e.target.value)} /></Col>
        <Col md={6}><Form.Label>Deductions 80C, etc. (₹)</Form.Label><Form.Control type="number" value={deductions} onChange={(e) => setDeductions(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Taxable Income" value={inr(taxable)} />
        <ResultRow label="Income Tax" value={inr(tax)} />
        <ResultRow label="Health & Edu Cess (4%)" value={inr(cess)} />
        <ResultRow label="Total Tax Payable" value={inr(tax + cess)} strong />
      </div>
    </div>
  );
}

/* ============ INTEREST ============ */
function SimpleInterestCalculator() {
  const [p, setP] = useState(100000);
  const [r, setR] = useState(8);
  const [t, setT] = useState(3);
  const si = (p * r * t) / 100;
  return (
    <div>
      <p className="text-muted-2">Calculate simple interest on a principal amount.</p>
      <Row className="g-3">
        <Col md={4}><Form.Label>Principal (₹)</Form.Label><Form.Control type="number" value={p} onChange={(e) => setP(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Rate (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={r} onChange={(e) => setR(+e.target.value)} /></Col>
        <Col md={4}><Form.Label>Time (years)</Form.Label><Form.Control type="number" value={t} onChange={(e) => setT(+e.target.value)} /></Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Simple Interest" value={inr(si)} />
        <ResultRow label="Total Amount" value={inr(p + si)} strong />
      </div>
    </div>
  );
}

function CompoundInterestCalculator() {
  const [p, setP] = useState(100000);
  const [r, setR] = useState(8);
  const [t, setT] = useState(3);
  const [freq, setFreq] = useState(1);
  const amount = p * Math.pow(1 + r / 100 / freq, freq * t);
  return (
    <div>
      <p className="text-muted-2">Calculate compound interest with your chosen compounding frequency.</p>
      <Row className="g-3">
        <Col md={3}><Form.Label>Principal (₹)</Form.Label><Form.Control type="number" value={p} onChange={(e) => setP(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Rate (% p.a.)</Form.Label><Form.Control type="number" step="0.1" value={r} onChange={(e) => setR(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Time (years)</Form.Label><Form.Control type="number" value={t} onChange={(e) => setT(+e.target.value)} /></Col>
        <Col md={3}><Form.Label>Compounding</Form.Label>
          <Form.Select value={freq} onChange={(e) => setFreq(+e.target.value)}><option value={1}>Yearly</option><option value={2}>Half-Yearly</option><option value={4}>Quarterly</option><option value={12}>Monthly</option></Form.Select>
        </Col>
      </Row>
      <div className="calc-result mt-4">
        <ResultRow label="Compound Interest" value={inr(amount - p)} />
        <ResultRow label="Total Amount" value={inr(amount)} strong />
      </div>
    </div>
  );
}

// ---- Groups registry ----
export const calculatorGroups = [
  {
    slug: "loan-emi-calculators",
    name: "Loan & EMI Calculators",
    icon: <img src={loancal} alt="Loan calculator" className="calc-svg-icon" />,
    image: emiImg,
    color: "#1e73be",
    short: "Work out loan EMIs and how much loan you're eligible for.",
    items: [
      { key: "emi", title: "EMI Calculator", Component: EmiCalculator },
      { key: "eligibility", title: "Loan Eligibility", Component: LoanEligibilityCalculator },
    ],
    about: [
      { type: "p", text: "Loan calculators help you plan your borrowing. The EMI calculator shows your fixed monthly instalment, while the eligibility calculator estimates the maximum loan you can get based on your income." },
      { type: "h2", text: "Tips" },
      { type: "ul", items: [
        "A longer tenure lowers EMI but increases total interest.",
        "Lenders usually allow up to ~50% of income toward EMIs.",
        "Prepaying early saves the most interest.",
      ]},
    ],
  },
  {
    slug: "investment-calculators",
    name: "Investment Calculators",
    icon: <FaChartLine />,
    image: investImg,
    color: "#16a34a",
    short: "SIP, lumpsum, FD and RD returns and maturity.",
    items: [
      { key: "sip", title: "SIP", Component: SipCalculator },
      { key: "lumpsum", title: "Lumpsum", Component: LumpsumCalculator },
      { key: "fd", title: "FD", Component: FdCalculator },
      { key: "rd", title: "RD", Component: RdCalculator },
    ],
    about: [
      { type: "p", text: "Investment calculators estimate how your money can grow through monthly SIPs, one-time lumpsum investments, fixed deposits or recurring deposits." },
      { type: "h2", text: "Tips" },
      { type: "ul", items: [
        "Start early compounding rewards time the most.",
        "SIPs average out market ups and downs.",
        "FD/RD returns are fixed; mutual-fund returns vary.",
      ]},
    ],
  },
  {
    slug: "tax-calculators",
    name: "Tax Calculators",
    icon: <FaReceipt />,
    image: taxImg,
    color: "#db2777",
    short: "GST and income tax estimates.",
    items: [
      { key: "gst", title: "GST", Component: GstCalculator },
      { key: "income-tax", title: "Income Tax", Component: IncomeTaxCalculator },
    ],
    about: [
      { type: "p", text: "Tax calculators help you work out GST on goods and services, and estimate your annual income tax liability." },
      { type: "h2", text: "Note" },
      { type: "ul", items: [
        "GST is split into CGST + SGST for intra-state sales.",
        "Income tax here uses the old regime with standard deduction & 87A rebate.",
        "Always confirm with a tax professional for filing.",
      ]},
    ],
  },
  {
    slug: "interest-calculators",
    name: "Interest Calculators",
    icon: <FaPercent />,
    image: interestImg,
    color: "#ca8a04",
    short: "Simple and compound interest calculations.",
    items: [
      { key: "simple", title: "Simple Interest", Component: SimpleInterestCalculator },
      { key: "compound", title: "Compound Interest", Component: CompoundInterestCalculator },
    ],
    about: [
      { type: "p", text: "Interest calculators show how much interest you'll earn or pay. Simple interest is calculated only on the principal, while compound interest is calculated on principal plus accumulated interest." },
      { type: "h2", text: "Formulas" },
      { type: "ul", items: [
        "Simple Interest = (P × R × T) / 100",
        "Compound Interest = P × (1 + R/n)^(n×T) − P",
      ]},
    ],
  },
];

export const getCalculatorGroup = (slug) => calculatorGroups.find((g) => g.slug === slug);
